import { useState, useCallback, useEffect } from 'react';
import { canFinalizarPagamento, buildPagamentoData } from '../services/pagamentoService';

// pagamentoInicial: boleto/PIX já gerado num acesso anterior ({ tipoPagamento,
// tituloData }) — ao retomar um pedido salvo, mostra o mesmo título em vez de
// gerar uma segunda cobrança.
export const usePagamento = (paymentConfig, onComplete, onGerarTitulo, pagamentoInicial = null) => {
  const [tipoPagamento, setTipoPagamento] = useState(pagamentoInicial?.tipoPagamento || null);
  const [boletoGerado, setBoletoGerado] = useState(Boolean(pagamentoInicial?.tituloData));
  const [tituloData, setTituloData] = useState(pagamentoInicial?.tituloData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const valorTaxa = paymentConfig?.amount || 0;

  // Auto-complete quando taxa é zero
  useEffect(() => {
    if (valorTaxa === 0) {
      const timer = setTimeout(() => {
        onComplete({ isento: true, valor: 0 });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [valorTaxa, onComplete]);

  // Gera o boleto/PIX de verdade (linha digitável, PIX copia-e-cola, link do
  // boleto) via API de Títulos. A partir daqui já dá pra finalizar — o
  // pagamento em si acontece fora do portal, não existe uma "confirmação"
  // pra pessoa fazer aqui.
  const handleGerarBoleto = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resultado = await onGerarTitulo(valorTaxa, tipoPagamento);
      setTituloData(resultado);
      setBoletoGerado(true);
    } catch (err) {
      setError(err.message || 'Erro ao gerar título de cobrança');
    } finally {
      setLoading(false);
    }
  }, [onGerarTitulo, valorTaxa, tipoPagamento]);

  const handleFinalizar = useCallback(async () => {
    setLoading(true);

    try {
      const pagamentoData = buildPagamentoData(tipoPagamento, valorTaxa, tituloData);
      await onComplete(pagamentoData);
    } catch (err) {
      setError(err.message || 'Erro ao processar pagamento');
    } finally {
      setLoading(false);
    }
  }, [tipoPagamento, valorTaxa, tituloData, onComplete]);

  const podeFinalizar = canFinalizarPagamento(tipoPagamento, boletoGerado);

  return {
    tipoPagamento,
    boletoGerado,
    tituloData,
    loading,
    error,
    valorTaxa,
    podeFinalizar,
    setTipoPagamento,
    handleGerarBoleto,
    handleFinalizar
  };
};
